import { globalStore } from '@thewhite/react-global-module/lib/store';
import isEqual from 'lodash.isequal';
import { Provider } from 'mobx-react';
import * as React from 'react';
import { HashRouter, Route, Switch } from 'react-router-dom';
import DICTIONARIES from '../modules/Dictionaries/enums/dictionaries';
import Preloader from '../modules/Global/components/preloader/preloader';
import NotFound from '../modules/Global/notFoundContainer';
import { MainMenuService } from '../modules/Global/services/mainMenuService';
import linkStore from '../modules/Global/store/linkStore';
import oktmoStore from '../modules/Global/store/oktmoStore';
import refStore from '../modules/Global/store/refStore';
import userStore from '../modules/Global/store/userStore';
import transactionOwnerStore from '../modules/Transaction/stores/transactionOwnerStore';
import MainWrap from '../modules/Wrappers/mainWrap/mainWrapComponent';

interface IMainRouterProps {
    history?: History;
    location?: Location;
}

const stores = { globalStore, refStore, userStore, transactionOwnerStore, linkStore, oktmoStore };

export default class MainRouter extends React.Component<IMainRouterProps> {
    shouldComponentUpdate(nextProps: Readonly<{ history?: History; location?: Location }>): boolean {
        return !isEqual(this.props.location, nextProps.location);
    }

    render() {
        const LoginPage = React.lazy(() => import('../modules/Auth/pages/login/login'));
        const MainPage = React.lazy(() => import('../modules/Main/pages/mainPage/mainPage'));
        const UserList = React.lazy(() => import('../modules/User/pages/userList/userListContainer'));
        const EcoForumUserList = React.lazy(() => import('../modules/User/pages/ecoForumUserList/ecoForumUserList'));
        const UserCard = React.lazy(() => import('../modules/User/pages/userCard/userCardContainer'));
        const TransactionList = React.lazy(() => import('../modules/Transaction/pages/transactionList/transactionListContainer'));
        const RegistrationRequestList = React.lazy(() => import('../modules/RegistrationRequest/pages/registrationRequestList/registrationRequestListContainer'));
        const RegistrationRequestCard = React.lazy(() => import('../modules/RegistrationRequest/pages/registrationRequestCard/registrationRequestCardContainer'));
        const RegistrationRequestEdit = React.lazy(() => import('../modules/RegistrationRequest/pages/registrationRequestEdit/registrationRequestEditContainer'));
        const InformationMessageList = React.lazy(() => import('../modules/InformationCenter/pages/informationMessages/informationMessagesList/informationMessagesListContainer'));
        const InformationMessageCard = React.lazy(() => import('../modules/InformationCenter/pages/informationMessages/informationMessagesCard/informationMessagesCardContainer'));
        const RecipientsGroupList = React.lazy(() => import('../modules/InformationCenter/pages/recipientsGroup/recipientsGroupList/recipientsGroupListContainer'));
        const RecipientsGroupCard = React.lazy(() => import('../modules/InformationCenter/pages/recipientsGroup/recipientsGroupCard/recipientsGroupCardContainer'));
        const OrganizationList = React.lazy(() => import('../modules/Organization/pages/organizationList/organizationListContainer'));
        const SocialCardList = React.lazy(() => import('../modules/SocialCard/pages/socialCardListContainer'));
        const organizationCardWrap = React.lazy(() => import('../modules/Organization/pages/organizationCardWrap/organizationCardWrapComponent'));
        const organizationGroupUsersList = React.lazy(() => import('../modules/Organization/pages/organizationGroupUserList/organizationGroupUserListContainer'));
        const EventList = React.lazy(() => import('../modules/Activities/pages/eventList/eventListContainer'));
        const EventCard = React.lazy(() => import('../modules/Activities/pages/eventCard/eventCardContainer'));
        const PurchasesList = React.lazy(() => import('../modules/Purchases/pages/purchasesList/purchasesListContainer'));
        const PurchaseCard = React.lazy(() => import('../modules/Purchases/pages/purchaseCard/purchaseCardContainer'));
        const ServicesList = React.lazy(() => import('../modules/Services/pages/servicesList/servicesListContainer'));
        const ServicesCard = React.lazy(() => import('../modules/Services/pages/serviceCard/serviceCardContainer'));
        const Reports = React.lazy(() => import('../modules/Analytics/pages/reports/reportsContainer'));
        const ActivitiesSystemicList = React.lazy(() => import('../modules/Activities/pages/systemicList/systemicListContainer'));
        const ActivitiesSystemicCard = React.lazy(() => import('../modules/Activities/pages/systemicCard/systemicCardContainer'));
        const ActivitiesIntegrationList = React.lazy(() => import('../modules/Activities/pages/integrationList/integrationListContainer'));
        const ActivitiesIntegrationCard = React.lazy(() => import('../modules/Activities/pages/integrationCard/integrationCardContainer'));
        const ActivitiesStatusList = React.lazy(() => import('../modules/Activities/pages/statusList/statusListContainer'));
        const ActivitiesStatusCard = React.lazy(() => import('../modules/Activities/pages/statusCard/statusCardContainer'));
        const ActivitiesApplicationsList = React.lazy(() => import('../modules/Activities/pages/applicationsList/applicationsListContainer'));
        const ActivitiesApplicationsCard = React.lazy(() => import('../modules/Activities/pages/applicationCard/applicationCardContainer'));
        const ActivityCategoryList = React.lazy(() => import('../modules/Dictionaries/pages/activityCategory/activityCategoryList/activityCategoryListContainer'));
        const ActivityCategoryCard = React.lazy(() => import('../modules/Dictionaries/pages/activityCategory/activityCategoryCard/activityCategoryCardComponent'));
        const AchievementsPage = React.lazy(() => import('../modules/Achievements/pages/achievementsPage'));
        const AchievementsCard = React.lazy(() => import('../modules/Achievements/pages/achievementCardPage'));
        const ServiceCategoryList = React.lazy(() => import('../modules/Dictionaries/pages/serviceCategory/serviceCategoryList/serviceCategoryListContainer'));
        const ServiceCategoryCard = React.lazy(() => import('../modules/Dictionaries/pages/serviceCategory/serviceCategoryCard/serviceCategoryCardComponent'));
        const InterestList = React.lazy(() => import('../modules/Dictionaries/pages/interest/interestList/interestListContainer'));
        const InterestCard = React.lazy(() => import('../modules/Dictionaries/pages/interest/interestCard/interestCardComponent'));
        const SocialStatusList = React.lazy(() => import('../modules/Dictionaries/pages/socialStatus/socialStatusList/socialStatusListContainer'));
        const SocialStatusCard = React.lazy(() => import('../modules/Dictionaries/pages/socialStatus/socialStatusCard/socialStatusCardComponent'));
        const OrganizationCategoryList = React.lazy(() => import('../modules/Dictionaries/pages/organizationCategory/organizationCategoryList/organizationCategoryListContainer'));
        const OrganizationCategoryCard = React.lazy(() => import('../modules/Dictionaries/pages/organizationCategory/organizationCategoryCard/organizationCategoryCardComponent'));
        const ProfessionList = React.lazy(() => import('../modules/Dictionaries/pages/profession/professionList/professionListContainer'));
        const ProfessionCard = React.lazy(() => import('../modules/Dictionaries/pages/profession/professionCard/professionCardComponent'));
        const RegistryDocumentTypeList = React.lazy(() => import('../modules/Dictionaries/pages/registryDocumentType/registryDocumentTypeList/registryDocumentTypeListContainer'));
        const RegistryDocumentTypeCard = React.lazy(() => import('../modules/Dictionaries/pages/registryDocumentType/registryDocumentTypeCard/registryDocumentTypeCardComponent'));
        const ActivityDocumentTypeList = React.lazy(() => import('../modules/Dictionaries/pages/activityDocumentType/activityDocumentTypeList/activityDocumentTypeListContainer'));
        const ActivityDocumentTypeCard = React.lazy(() => import('../modules/Dictionaries/pages/activityDocumentType/activityDocumentTypeCard/activityDocumentTypeCardComponent'));
        const RegistrationApplicationsList = React.lazy(() => import('../modules/PartnerApplications/pages/registationApplications/registationApplicationsContainer'));
        const ActivityApplicationsList = React.lazy(() => import('../modules/PartnerApplications/pages/activityApplicationsList/activityApplicationsListContainer'));
        const ServiceApplicationsList = React.lazy(() => import('../modules/PartnerApplications/pages/servicesApplicationsList/servicesApplicationsListContainer'));
        const SystemSettings = React.lazy(() => import('../modules/SystemSettings/pages/systemSettingsContainer'));
        const RegisterBeneficiariesList = React.lazy(() => import('../modules/RegisterBeneficiaries/pages/list/registerBeneficiariesListContainer'));
        const RegisterBeneficiariesCard = React.lazy(() => import('../modules/RegisterBeneficiaries/pages/card/registerBeneficiariesCardContainer'));
        const RetailChainsList = React.lazy(() => import('../modules/Dictionaries/pages/retailChains/retailChainsList/retailChainsListContainer'));
        const ManagementRegistry = React.lazy(() => import('../modules/ManagmentRegistry/pages/managementRegistryContainer'));
        const ReportSocialRegistry = React.lazy(() => import('../modules/ReportSocialRegistry/pages/reportSocialRegistryContainer'));
        const ClosedRegistryPeriod = React.lazy(() => import('../modules/ClosedRegistry/pages/closedRegistryContainer'));
        const MunicipalAreas = React.lazy(() => import('../modules/MunicipalAreas/pages/municipalAreaContainer'));
        const Scoring = React.lazy(() => import('../modules/Activities/pages/scoring/scoringContainer'));
        const NewsCategoryList = React.lazy(() => import('../modules/News/pages/newsCategoryList/newsCategoryListContainer'));
        const NewsCategoryCard = React.lazy(() => import('../modules/News/pages/newsCategoryCard/newsCategoryCardComponent'));
        const NewsList = React.lazy(() => import('../modules/News/pages/newsList/newsListContainer'));
        const NewsCard = React.lazy(() => import('../modules/News/pages/newsCard/newsCardContainer'));
        const PollList = React.lazy(() => import('../modules/Polls/pages/pollList/pollListContainer'));
        const PollCard = React.lazy(() => import('../modules/Polls/pages/pollCard/pollCardContainer'));
        const LocationList = React.lazy(() => import('../modules/Locations/pages/locationList'));
        const LocationCard = React.lazy(() => import('../modules/Locations/pages/locationCard'));
        const MapCard = React.lazy(() => import('../modules/Map/pages/mapCard'));

        return (
            <Provider
                {...stores}
            >
                <HashRouter>
                    <React.Suspense
                        fallback={
                            <Preloader/>
                        }
                    >
                        <Switch>
                            <Route
                                exact
                                path={'/login'}
                                component={LoginPage}
                            />
                            <MainWrap
                                location={this.props.location}
                            >
                                <React.Suspense
                                    fallback={
                                        <Preloader/>
                                    }
                                >
                                    <Switch>
                                        <Route
                                            exact
                                            path={'/'}
                                            component={MainPage}
                                        />
                                        <Route
                                            exact
                                            path={'/users'}
                                            component={UserList}
                                        />
                                        <Route
                                            exact
                                            path={'/eco-users'}
                                            component={EcoForumUserList}
                                        />
                                        <Route
                                            exact
                                            path={'/users/:marker'}
                                            component={UserCard}
                                        />
                                        <Route
                                            exact
                                            path={'/transactions'}
                                            component={TransactionList}
                                        />
                                        <Route
                                            exact
                                            path={'/registration-request'}
                                            component={RegistrationRequestList}
                                        />
                                        <Route
                                            exact
                                            path={'/registration-request/card/:id'}
                                            component={RegistrationRequestCard}
                                        />
                                        <Route
                                            exact
                                            path={'/registration-request/card/:id/edit'}
                                            component={RegistrationRequestEdit}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.informationMessageRoutePath}/messages-list`}
                                            component={InformationMessageList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.informationMessageRoutePath}/messages-list/:id`}
                                            component={InformationMessageCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.informationMessageRoutePath}/recipients-groups`}
                                            component={RecipientsGroupList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.informationMessageRoutePath}/recipients-group/:id`}
                                            component={RecipientsGroupCard}
                                        />
                                        <Route
                                            exact
                                            path={'/organizations'}
                                            component={OrganizationList}
                                        />
                                        <Route
                                            exact
                                            path={'/social-cards'}
                                            component={SocialCardList}
                                        />
                                        <Route
                                            exact
                                            path={'/organizations/card/:id?'}
                                            component={organizationCardWrap}
                                        />
                                        <Route
                                            exact
                                            path={'/organizations/card/:organizationId/group/:groupId/user-list'}
                                            component={organizationGroupUsersList}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/event'}
                                            component={EventList}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/event/card/:id?'}
                                            component={EventCard}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/systemic'}
                                            component={ActivitiesSystemicList}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/systemic/card/:id?'}
                                            component={ActivitiesSystemicCard}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/integration'}
                                            component={ActivitiesIntegrationList}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/integration/card/:id?'}
                                            component={ActivitiesIntegrationCard}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/status'}
                                            component={ActivitiesStatusList}
                                        />
                                        <Route
                                            exact
                                            path={'/activities/status/card/:id?'}
                                            component={ActivitiesStatusCard}
                                        />
                                        <Route
                                            exact
                                            path={'/applications'}
                                            component={ActivitiesApplicationsList}
                                        />
                                        <Route
                                            exact
                                            path={'/applications/card/:id?'}
                                            component={ActivitiesApplicationsCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.achievementsRoutePath}`}
                                            component={AchievementsPage}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.achievementsRoutePath}/card/:id?`}
                                            component={AchievementsCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.partnerApplicationsRoutPath}/registration`}
                                            component={RegistrationApplicationsList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.partnerApplicationsRoutPath}/activity`}
                                            component={ActivityApplicationsList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.partnerApplicationsRoutPath}/service`}
                                            component={ServiceApplicationsList}
                                        />
                                        <Route
                                            exact
                                            path={'/analytics/reports'}
                                            component={Reports}
                                        />
                                        <Route
                                            exact
                                            path={'/purchases'}
                                            component={PurchasesList}
                                        />
                                        <Route
                                            exact
                                            path={'/purchases/card/:id?'}
                                            component={PurchaseCard}
                                        />
                                        <Route
                                            exact
                                            path={'/services'}
                                            component={ServicesList}
                                        />
                                        <Route
                                            exact
                                            path={'/services/card/:id?'}
                                            component={ServicesCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ACTIVITY_CATEGORY.path}`}
                                            component={ActivityCategoryList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ACTIVITY_CATEGORY.path}/card/:id?`}
                                            component={ActivityCategoryCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.SERVICE_CATEGORY.path}`}
                                            component={ServiceCategoryList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.SERVICE_CATEGORY.path}/card/:id?`}
                                            component={ServiceCategoryCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.INTEREST.path}`}
                                            component={InterestList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.INTEREST.path}/card/:id?`}
                                            component={InterestCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.SOCIAL_STATUS.path}`}
                                            component={SocialStatusList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.SOCIAL_STATUS.path}/card/:id?`}
                                            component={SocialStatusCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ORGANIZATION_CATEGORY.path}`}
                                            component={OrganizationCategoryList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ORGANIZATION_CATEGORY.path}/card/:id?`}
                                            component={OrganizationCategoryCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.PROFESSION.path}`}
                                            component={ProfessionList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.PROFESSION.path}/card/:id?`}
                                            component={ProfessionCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.REGISTRY_DOCUMENT_TYPE.path}`}
                                            component={RegistryDocumentTypeList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.REGISTRY_DOCUMENT_TYPE.path}/card/:id?`}
                                            component={RegistryDocumentTypeCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ACtIVITY_DOCUMENT_TYPE.path}`}
                                            component={ActivityDocumentTypeList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.ACtIVITY_DOCUMENT_TYPE.path}/card/:id?`}
                                            component={ActivityDocumentTypeCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.dictionariesRoutePath}/${DICTIONARIES.RETAIL_CHAINS.path}`}
                                            component={RetailChainsList}
                                        />
                                        <Route
                                            exact
                                            path={'/system-settings'}
                                            component={SystemSettings}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/list-beneficiaries`}
                                            component={RegisterBeneficiariesList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/card/:id`}
                                            component={RegisterBeneficiariesCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/management-registry`}
                                            component={ManagementRegistry}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/report-social-registry`}
                                            component={ReportSocialRegistry}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/closed-registry`}
                                            component={ClosedRegistryPeriod}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.registerOfBeneficiariesRoutePath}/municipal-areas`}
                                            component={MunicipalAreas}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.activitiesRoutePath}/scoring`}
                                            component={Scoring}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.newsRoutePath}/categories`}
                                            component={NewsCategoryList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.newsRoutePath}/categories/card/:id?`}
                                            component={NewsCategoryCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.newsRoutePath}/list`}
                                            component={NewsList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.newsRoutePath}/card/:id?`}
                                            component={NewsCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.pollRoutePath}`}
                                            component={PollList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.pollRoutePath}/card/:pollId?`}
                                            component={PollCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.locationsRoutePath}`}
                                            component={LocationList}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.locationsRoutePath}/card/:id?`}
                                            component={LocationCard}
                                        />
                                        <Route
                                            exact
                                            path={`/${MainMenuService.mapRoutePath}`}
                                            component={MapCard}
                                        />
                                        <Route
                                            component={NotFound}
                                        />
                                    </Switch>
                                </React.Suspense>
                            </MainWrap>
                        </Switch>
                    </React.Suspense>
                </HashRouter>
            </Provider>
        );
    }
}
